import { ApolloError, gql } from '@apollo/client';
import { Box } from '@chakra-ui/react';
import { t } from 'i18next';
import { isNil } from 'lodash';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '~auth/useAuth';
import { CustomerClaimCodeTable_ClaimCodeFragment } from '~components/displays/ClaimCodeTable/__generated__/ClaimCodeTable.graphql';
import { DefaultConfigurationCell_CustomerFragment } from '~components/displays/ClaimCodeTable/Cell/__generated__/DefaultConfigurationCell.graphql';
import {
  ClaimCodeTable,
  useClaimCodeTable,
} from '~components/displays/ClaimCodeTable/ClaimCodeTable';
import {
  ClaimCodeTableContext,
  ClaimCodeTableProvider,
} from '~components/displays/ClaimCodeTable/ClaimCodeTableContext';
import {
  Columns,
  FilterOption,
  getValidityFilterOptions,
} from '~components/displays/ClaimCodeTable/constants';
import {
  FilterContext,
  getPersistentFilters,
  useFilterPersistenceManager,
} from '~components/displays/DisplayTable/persistantFilters/persistantFilters';
import { DisplaysQueryContext } from '~components/displays/DisplayTable/useDisplaysQuery';
import { BackButton } from '~components/ui/BackButton';
import { Page } from '~components/ui/Page';
import { PageActions } from '~components/ui/PageActions';
import { PageContent } from '~components/ui/PageContent';
import { PageHeading } from '~components/ui/PageHeading';
import { PageLoader } from '~components/ui/PageLoader';
import { HandleApiError } from '~graphql/HandleApiError';
import { ensure, isDefined } from '~utils/types';
import { ClaimCodePageQuery, useClaimCodePageQuery } from './__generated__/claimCode.graphql';

export type DataWithGroupSite = Omit<ClaimCodePageQuery, 'customer'> & {
  customer: ClaimCodePageQuery['customer'];
};

export type MultiClaimFilters = Array<{ id: string; value: FilterOption[] }>;

type CustomerClaimCodeOverviewProps = {
  data: DataWithGroupSite;
  error: ApolloError | undefined;
  pageIndex: number;
  pageSize: number;
  filters: MultiClaimFilters;
  totalRowCount: number;
  search: string;
  handleSetSearch: (searchText: string) => void;
  handleSetPageSize: (pageSize: number) => void;
  handleSetPageIndex: (pageIndex: number) => void;
  handleSetFilters: (filterId: string, newValue: FilterOption[], resetPagination?: boolean) => void;
};

function ClaimCodeOverviewHeading() {
  const navigate = useNavigate();

  const tableContext = useContext(ClaimCodeTableContext);
  if (isNil(tableContext)) {
    throw new Error('TableContext can only be used within TableContext Provider');
  }

  const handleGoBack = () => {
    navigate('../displays');
  };

  return (
    <>
      <PageHeading floatingButton={<BackButton onClick={handleGoBack} />}>
        {t('claimCodeHistory')}
      </PageHeading>
    </>
  );
}

function ClaimCodeOverview({
  data,
  pageIndex,
  pageSize,
  filters,
  handleSetPageSize,
  handleSetPageIndex,
  totalRowCount,
  handleSetFilters,
  search,
  handleSetSearch,
}: CustomerClaimCodeOverviewProps) {
  const navigate = useNavigate();
  const table = useClaimCodeTable({
    data: data?.customer?.multiClaimCodes ?? [],
    customerData: (data?.customer as CustomerClaimCodeTable_ClaimCodeFragment) || [],
    pageIndex,
    pageSize,
    totalRowCount,
  });
  const { setDisplaysQuery } = useContext(DisplaysQueryContext);
  const tableContext = useContext(ClaimCodeTableContext);

  if (isNil(tableContext)) {
    throw new Error('TableContext can only be used within TableContext Provider');
  }
  const { setTable } = tableContext;

  useEffect(() => {
    setTable?.(table);
  }, [table, setTable]);

  async function handleClaimCodeClicked(id: string, code: string, name: string) {
    if (!setDisplaysQuery) return;
    const combinedClaimCode = `${code}|${name}|${id}`;
    const newQueryParams = new URLSearchParams();
    newQueryParams.set('claimCode', combinedClaimCode);
    setDisplaysQuery(newQueryParams);
    navigate(`../displays?claimCode=${encodeURIComponent(combinedClaimCode)}`);
  }

  return (
    <>
      <PageActions
        left={
          <>
            <ClaimCodeTable.Filter
              table={table}
              data={data}
              filtersState={filters}
              handleSetFilters={handleSetFilters}
            />
          </>
        }
        searchbar={
          <ClaimCodeTable.SearchSelector search={search} handleSetSearch={handleSetSearch} />
        }
      />
      <Box
        boxShadow="elevated"
        border="1px solid"
        borderRadius="md"
        borderColor="gray.100"
        overflowX="auto"
        bgColor="white"
      >
        <ClaimCodeTable
          table={table}
          customerData={data.customer as DefaultConfigurationCell_CustomerFragment}
          onGoToDisplays={(id, code, name) => handleClaimCodeClicked(id, code, name)}
        />
      </Box>
      <Box display="flex" marginTop="4">
        <Box flex="1">
          <ClaimCodeTable.PageSizeSelector
            table={table}
            handleSetPageSize={handleSetPageSize}
            pageSize={pageSize}
          />
        </Box>
        <ClaimCodeTable.Pagination
          pageIndex={pageIndex}
          totalRowCount={totalRowCount}
          handleSetPageIndex={handleSetPageIndex}
          pageSize={pageSize}
        />
      </Box>
    </>
  );
}
export function ClaimCodePage() {
  const { customer } = useParams();
  const { user } = useAuth();

  const customerHandle = ensure(customer);
  const [pageSize, setPageSize] = useState(25);
  const [pageIndex, setPageIndex] = useState(0);
  const [search, setSearch] = useState('');

  // Track whether initial persisted filters have been applied
  const [isFiltersInitialized, setIsFiltersInitialized] = useState(false);
  const filtersAppliedRef = useRef(false);

  const [filters, setFilters] = useState<MultiClaimFilters>([
    { id: 'groups', value: [] },
    { id: 'site', value: [] },
    { id: 'validity', value: [] },
  ]);

  interface TransformedResult {
    groupIds: string[];
    siteIds: string[];
    validity: string[];
  }
  function transformFiltersData(filters: MultiClaimFilters): TransformedResult {
    const transformed: TransformedResult = {
      groupIds: [],
      siteIds: [],
      validity: [],
    };

    filters.forEach((filter) => {
      switch (filter.id) {
        case 'groups':
          transformed.groupIds = filter.value.map((option) => option.value);
          break;
        case 'site':
          transformed.siteIds = filter.value.map((option) => option.value);
          break;
        case 'validity':
          transformed.validity = filter.value.map((option) => option.value);
          break;
        default:
          break;
      }
    });

    return transformed;
  }
  const transformedFilters = transformFiltersData(filters);
  const handleSetPageSize = (pageSize: number) => {
    setPageSize(pageSize);
  };
  const handleSetPageIndex = (pageIndex: number) => {
    setPageIndex(pageIndex);
  };

  const handleSetFilters = (filterId: string, newValue: FilterOption[], resetPagination = true) => {
    if (resetPagination) {
      setPageIndex(0);
    }
    setFilters((prevFilters) =>
      prevFilters.map((filter) =>
        filter.id === filterId ? { ...filter, value: newValue } : filter,
      ),
    );
  };

  const handleSetSearch = (searchText: string) => {
    setSearch(searchText);
  };

  // Load persisted filters from localStorage using customerHandle (from URL)
  // This runs BEFORE the query, using URL handle instead of GraphQL customer.id
  useEffect(() => {
    // Only run once per customer
    if (filtersAppliedRef.current || !customerHandle || !user?.subId) {
      if (!customerHandle) {
        setIsFiltersInitialized(true);
      }
      return;
    }

    async function loadAndApplyFilters() {
      if (!user?.subId || !customerHandle) return;

      // Use customer handle from URL (not customer.id from GraphQL)
      const persisted = await getPersistentFilters(
        user.subId,
        customerHandle,
        FilterContext.CLAIM_CODES,
      );

      // If we have persisted filters, apply them
      if (Object.keys(persisted).length > 0) {
        setFilters((prevFilters) =>
          prevFilters.map((filter) => {
            const persistedValues = persisted[filter.id];
            if (persistedValues && persistedValues.length > 0) {
              return {
                ...filter,
                value: persistedValues.map((v) => ({
                  ...v,
                  column: filter.id,
                })) as FilterOption[],
              };
            }
            return filter;
          }),
        );
      }

      filtersAppliedRef.current = true;
      setIsFiltersInitialized(true);
    }

    loadAndApplyFilters();
  }, [customerHandle, user]);

  const { data, loading, error } = useClaimCodePageQuery({
    variables: {
      customerHandle,
      pageNumber: pageIndex + 1,
      pageSize: pageSize,
      searchTerm: search,
      filter: transformedFilters,
    },
    skip: !isFiltersInitialized, // Skip query until filters are loaded
  });

  const totalRowCount = data?.customer?.totalFilteredMultiClaimCount || 0;

  // All available filter options (not just enabled ones) for filter reconstruction
  const allAvailableFilters = useMemo<FilterOption[]>(() => {
    if (!data?.customer) return [];

    const groupOptions: FilterOption[] = (data.customer.groups ?? []).map((g) => ({
      value: g.id,
      label: g.name,
      column: Columns.Groups,
      id: g.id,
    }));

    const siteOptions: FilterOption[] = (data.customer.sites ?? []).map((s) => ({
      value: s.id,
      label: s.name,
      column: Columns.Site,
      id: s.id,
    }));

    return [...groupOptions, ...siteOptions, ...getValidityFilterOptions()];
  }, [data?.customer]);

  const backendMap = useMemo<Record<string, Set<string>>>(() => {
    if (!data?.customer)
      return {
        groups: new Set<string>(),
        site: new Set<string>(),
      };
    return {
      groups: new Set(data.customer.groups?.map((g) => g.id) ?? []),
      site: new Set(data.customer.sites?.map((s) => s.id) ?? []),
    };
  }, [data?.customer]);

  const filtersReady = data?.customer?.groups && data?.customer?.sites;

  const { createFilterHandler } = useFilterPersistenceManager<FilterOption>({
    userId: user?.subId ?? '',
    customerHandle: customerHandle ?? '',
    customerId: data?.customer?.id,
    context: FilterContext.CLAIM_CODES,
    allEnabledFilters: allAvailableFilters,
    backendMap,
    allowedFilterKeys: ['groups', 'site', 'validity'],
    onApplyFilters: handleSetFilters,
    filtersReady: !!filtersReady,
  });

  const handleSetFiltersWithPersistence = createFilterHandler<FilterOption>();

  return (
    <Page title="Claim codes" pageName="claimCode_overview">
      <ClaimCodeTableProvider>
        {!loading && !error && (
          <PageContent backgroundColor="gray.50">
            <ClaimCodeOverviewHeading />
          </PageContent>
        )}
        <PageContent>
          {loading || !isFiltersInitialized ? (
            <PageLoader />
          ) : error ? (
            <HandleApiError error={error} />
          ) : (
            isDefined(data?.customer) && (
              <ClaimCodeOverview
                data={data as DataWithGroupSite}
                error={error}
                pageSize={pageSize}
                pageIndex={pageIndex}
                totalRowCount={totalRowCount}
                filters={filters}
                search={search}
                handleSetSearch={handleSetSearch}
                handleSetPageSize={handleSetPageSize}
                handleSetPageIndex={handleSetPageIndex}
                handleSetFilters={handleSetFiltersWithPersistence}
              />
            )
          )}
        </PageContent>
      </ClaimCodeTableProvider>
    </Page>
  );
}

ClaimCodePage.graphql = {
  queries: {
    ClaimCodePage: gql`
      query ClaimCodePage(
        $customerHandle: String!
        $pageNumber: Int
        $pageSize: Int
        $searchTerm: String
        $filter: MultiClaimFilters
      ) {
        customer: customerByHandle(handle: $customerHandle) {
          id
          groups {
            id
            name
          }
          sites {
            name
            id
          }
          ...DefaultConfigurationCell_customer
          multiClaimCodes(
            pageNumber: $pageNumber
            pageSize: $pageSize
            searchTerm: $searchTerm
            filter: $filter
          ) {
            ...ClaimCodeTable_claimCode
            createdAt
            customerId
            organizationId
            state
            usageCount
          }
          totalFilteredMultiClaimCount(searchTerm: $searchTerm, filter: $filter)
        }
      }
    `,
  },
};
